/**
 * ★★★ A-7c — O RESULTADO DO DIA EM TEXTO, para colar no grupo do WhatsApp.
 *
 * ⚠⚠⚠ **ESTE MÓDULO NÃO CALCULA NADA.** Ele recebe o objeto que `montarGradeDiaria` já
 * devolveu e lê `grade.linhas[].celulas[i]`. ★ É a mesma regra escrita no topo de
 * `relatorios/formato.js` — e aqui ela é mais que estilo: **é o que faz o texto copiado ser,
 * por construção, o mesmo número da coluna do dia.** ⚠ Se alguém se pegar somando aqui, o
 * texto passou a poder divergir da tela, e o teste que compara os dois perde o sentido.
 *
 * ★★★ POR QUE OS FORMATADORES MORAM AQUI, e não em `formato.js`:
 *
 * `formato.moeda` imprime **centavos** (`R$ 11.550,00`), porque os relatórios da VD pediam
 * isso. ⚠ A grade imprime **sem centavos** (`R$ 11.550`). ★★ Então o texto do dia usa os
 * formatadores da GRADE — e `GradeDiaria.jsx` importa **estes**, daqui: uma implementação
 * só, usada pelas duas pontas. ⚠⚠ Duas cópias do mesmo `toLocaleString` seriam a porta para
 * a tela dizer `R$ 11.550` e o texto dizer `R$ 11.551`.
 *
 * ★ Regras herdadas e mantidas: sem tabela (WhatsApp não tem), linhas curtas, emoji só no
 * cabeçalho, denominador zero vira `—` e **nunca** `0%`, e **nenhum nome de paciente**.
 */

import { diaCurto } from '@/relatorios/formato';

const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * `R$ 11.550` — sem centavos, como a grade.
 *
 * ⚠⚠ O `replace` NÃO é cosmético: o `Intl` em pt-BR separa `R$` do número com um **espaço
 * inquebrável** (U+00A0 / U+202F). ★ Colado no WhatsApp isso vira um caractere que ninguém vê
 * e que quebra qualquer comparação de texto — ★★ é a mesma troca que `relatorios/formato.js`
 * faz em `moeda`, pelo mesmo motivo, e foi um teste de igualdade que a cobrou aqui.
 */
export const moedaCurta = (v) => Number(v ?? 0)
  .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
  .replace(/[\u00a0\u202f]/g, ' ');

/** `55,6%` — uma casa, como a grade. */
export const pct1 = (v) => `${Number(v ?? 0).toFixed(1).replace('.', ',')}%`;

/** Inteiro com até uma casa — `27`, `1,5`. */
export const inteiro = (v) => Number(v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/**
 * `18,5` — o dinheiro em MILHARES, que é o que a célula do dia mostra.
 *
 * ⚠ Medido: é a diferença entre **4** e **9** colunas de dia visíveis a 1366 px. O valor
 * inteiro continua acessível no `title` da célula e no texto copiado.
 */
export const milhares = (v) => (Number(v ?? 0) / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/** `null`/`undefined` é "não dá para saber" e vira travessão — nunca `0`. */
export function formatarCelula(valor, formato) {
  if (valor === null || valor === undefined) return '—';
  if (formato === 'milhares') return milhares(valor);
  if (formato === 'moeda') return moedaCurta(valor);
  if (formato === 'pct') return pct1(valor);
  return inteiro(valor);
}

/**
 * ★★ O DIA PADRÃO do copiador: **o último dia da grade que já aconteceu**.
 *
 * ★ No mês corrente isso é **hoje** (hoje nunca é `futuro`), ou o último dia útil anterior
 * quando hoje é sábado/domingo/feriado. ★ Num mês passado é o último dia dele. ⚠ Num mês
 * **futuro** todos os dias são `futuro` e a resposta é `null` — não há resultado a copiar.
 */
export function diaPadraoDaGrade(grade) {
  const dias = grade?.dias ?? [];
  for (let i = dias.length - 1; i >= 0; i -= 1) if (!dias[i].futuro) return dias[i].chave;
  return null;
}

/**
 * ★★ Os rótulos do TEXTO, e eles **não** são os da tela — de propósito.
 *
 * ⚠ Na tela a linha chama-se "Valor de vendas (R$ mil)", porque a célula mostra `18,5`.
 * ★★★ No texto o valor vai em **reais cheios** (decisão do dono), então o "(R$ mil)"
 * mentiria. ⚠ "Comparecimentos" leva "(baixas do dia)" porque é o relógio da linha — mais da
 * metade das baixas é de consulta de outro dia, e sem o rótulo o número parece errado.
 */
const ROTULOS = {
  leads: 'Leads que entraram',
  agendados: 'Agendamentos',
  comparecimentos: 'Comparecimentos (baixas do dia)',
  fechamentos: 'Fechamentos',
  valor: 'Valor de vendas',
  ticket: 'Ticket médio',
  conversao: 'Conversão de valor',
};

/** `✅`, `❌` — ou **nada**, quando não há meta (`atingiu === null`). */
const marca = (celula) => (celula?.atingiu === true ? ' ✅' : celula?.atingiu === false ? ' ❌' : '');

/** O trecho "(meta …)" de cada linha, ou "(sem meta)". */
function trechoDaMeta(linha, grade) {
  if (linha.id === 'leads') {
    const m = grade.funil?.leadsDia;
    return m === null || m === undefined ? '(sem meta)' : `(meta ${inteiro(m)})`;
  }
  if (linha.id === 'valor') {
    const m = grade.metaValorDia;
    return m === null || m === undefined ? '(sem meta)' : `(meta do dia ${moedaCurta(m)})`;
  }
  const alvo = Number(linha.meta?.valor);
  if (!(alvo > 0)) return '(sem meta)';
  if (linha.meta?.tipo === 'moeda') return `(meta ${moedaCurta(alvo)})`;
  return `(meta ${inteiro(alvo)}%)`;
}

/**
 * O valor do dia de cada linha, na forma que o dono pediu:
 *   contagem        → `27`
 *   contagem + taxa → `15 · 55,6%`
 *   dinheiro        → `R$ 11.550`  (⚠ reais CHEIOS, não os milhares da célula)
 *   taxa            → `29,6%`
 */
function trechoDoValor(linha, celula) {
  if (celula.valor === null || celula.valor === undefined) return '—';
  if (linha.id === 'valor' || linha.id === 'ticket') return moedaCurta(celula.valor);
  if (linha.formato === 'pct') return pct1(celula.valor);
  if (celula.taxa === null || celula.taxa === undefined) return inteiro(celula.valor);
  return `${inteiro(celula.valor)} · ${pct1(celula.taxa)}`;
}

/**
 * ★★★ O texto de UM dia da grade.
 *
 * @param grade  a saída de `montarGradeDiaria`
 * @param chave  `'AAAA-MM-DD'` — um dos `grade.dias`
 * @returns o texto, ou `''` quando a chave não está na grade
 */
export function textoDoDiaDaGrade(grade, chave) {
  const i = (grade?.dias ?? []).findIndex((d) => d.chave === chave);
  if (i < 0) return '';
  const dia = grade.dias[i];
  const linhas = [`📊 Resultado do dia — ${SEMANA[dia.diaSemana]} ${diaCurto(chave)}`];
  for (const linha of grade.linhas) {
    const celula = linha.celulas[i];
    if (!celula) continue;
    linhas.push(
      `${ROTULOS[linha.id] ?? linha.rotulo}: ${trechoDoValor(linha, celula)} `
      + `${trechoDaMeta(linha, grade)}${marca(celula)}`,
    );
  }
  return linhas.join('\n');
}
